import PropTypes from 'prop-types';
import React from 'react';
import BriefStore from 'contest/stores/brief-store';
import { connectToStores } from 'fluxible-addons-react';

import briefUpdateElementAction from 'contest/actions/brief-update-element';
import briefSaveElementAction from 'contest/actions/brief-save-element';

/**
 * Created a component connecting an inner component to a BriefElement
 *
 * connectEditElementToBriefStore will return a new React Component that wraps EditBriefElementComponent, connecting it to the
 * Fluxible brief store and retrieving the appropriate element data.
 *
 * It provides the following properties:
 *
 * - onChange(value, saveNow) — a callback the inner component should call when the value of the element changes.  The
 *   value is the first argument.  saveNow should be true to trigger saving the value to the backend immediately; this
 *   is equivalent to calling onChange(value); onSave(); except it's handled properly by Fluxible.
 * - onSave() — Trigger saving the value provided to onChange to the backend.
 *
 * @param EditBriefElementComponent — Component to wrap, should accept onChange and onSave props as a minimum
 * @param getPropsFromElement — A callback that should return an object of additional properties to pass through to the inner
 *        component.  The element object is provided as the only argument to this function.  Generally this should be
 *        used to introspect the element object and set derived properties of the inner component based on properties
 *        on the element.
 * @param processValue (optional) — called during onChange.  The value provided to onChange is the only argument.  This
 *        is your opportunity to intercept callbacks from the inner component back to the store to manipulate the return
 *        value as required.  The common use-case for this would be to transform the value before it's sent back to the
 *        server, in cases where the inner component may not work with the value in the format desired.
 * @returns A React Component that takes the following props:
 * - sectionKey — section of the brief to retrieve
 * - elementKey — element key of the brief to retrieve
 *
 * Usage example:
 *
 *     const InnerComponent = ...
 *     const ConnectedInnerComponent = connectEditElementToBriefStore(InnerComponent, element => ({
 *         value: element.value,
 *         choices: element.trans.choices
 *     }), value => value.filter(v => v));
 *     const element = <ConnectedInnerComponent sectionKey="foo" elementKey="bar" />;
 */
const connectEditElementToBriefStore = (
    EditBriefElementComponent,
    getPropsFromElement = element => ({}),
    processValue = value => value
) => {
    class BriefElementWrapper extends React.Component {
        constructor(props) {
            super(props);
            this.handleChange = this.handleChange.bind(this);
            this.handleSave = this.handleSave.bind(this);

            this.state = {
                dirty: false,
            };
        }

        handleChange(value, saveNow) {
            const processedValue = processValue(value);

            this.setState({ dirty: true });

            this.context.executeAction(briefUpdateElementAction, {
                section: this.props.sectionKey,
                element: this.props.elementKey,
                value: processedValue,
                saveNow: saveNow, // save immediately or not?
            });
        }

        handleSave() {
            if (this.state.dirty) {
                this.setState({ dirty: false });
                this.context.executeAction(briefSaveElementAction, {
                    section: this.props.sectionKey,
                    element: this.props.elementKey,
                });
            }
        }

        render() {
            const props = getPropsFromElement(this.props.element);
            return (
                <EditBriefElementComponent
                    element={this.props.element}
                    onChange={this.handleChange}
                    onSave={this.handleSave}
                    {...props}
                />
            );
        }
    }

    BriefElementWrapper.contextTypes = {
        executeAction: PropTypes.func.isRequired,
    };

    BriefElementWrapper.propTypes = {
        sectionKey: PropTypes.string.isRequired,
        elementKey: PropTypes.string.isRequired,
        element: PropTypes.object.isRequired,
    };

    const ConnectedBriefElement = connectToStores(
        BriefElementWrapper,
        [BriefStore],
        (context, props) => {
            const store = context.getStore(BriefStore);
            return {
                element: store.outline[props.sectionKey].elements[props.elementKey],
            };
        }
    );

    return ConnectedBriefElement;
};

export default connectEditElementToBriefStore;



// WEBPACK FOOTER //
// ./src/web/js/contest/components/connected/connect-edit-element-to-brief-store.jsx